import React, { useEffect, useRef, useState } from 'react';
import { BellRing, Clock, User, CalendarDays, Share2, Check } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

const DISMISS_KEY = 'sm-dismissed-reminders';
const getDismissed = () => { try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]'); } catch { return []; } };
const addDismissed = (id) => { const d = getDismissed().slice(-150); d.push(id); localStorage.setItem(DISMISS_KEY, JSON.stringify(d)); };

const playChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1108, 1318].forEach((freq, i) => {
      const t = i * 0.28;
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = 'sine'; o.frequency.value = freq;
      o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.55);
      o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.6);
    });
  } catch (e) {}
};

export const AppointmentReminder = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [sharedIds, setSharedIds] = useState([]);
  const soundedRef = useRef(new Set());

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const check = async () => {
      try {
        const { data } = await api.get('/appointments');
        const now = new Date();
        const dismissed = getDismissed();
        const mine = data.filter((a) => {
          if (a.status === 'Concluído' || dismissed.includes(a.id)) return false;
          const shared = (a.compartilhadoCom || []).some((u) => u.user_id === user.user_id);
          const isMine = shared || a.vendedor === user.name || a.criadoPorId === user.user_id || a.criadoPor === user.name || a.bdc_agent === user.name;
          if (!isMine || !a.data || !a.hora) return false;
          const dt = new Date(`${a.data}T${a.hora}:00`);
          if (isNaN(dt)) return false;
          const diffMin = (dt - now) / 60000;
          return diffMin > 0 && diffMin <= 60;
        });
        if (!alive) return;
        setAlerts(mine);
        mine.forEach((a) => { if (!soundedRef.current.has(a.id)) { soundedRef.current.add(a.id); playChime(); } });
      } catch (e) {}
    };
    check();
    const iv = setInterval(check, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, [user]);

  if (alerts.length === 0) return null;
  const a = alerts[0];
  const dt = new Date(`${a.data}T${a.hora}:00`);
  const minsLeft = Math.max(1, Math.round((dt - new Date()) / 60000));

  const dismiss = () => {
    addDismissed(a.id);
    setAlerts((prev) => prev.filter((x) => x.id !== a.id));
    setShareOpen(false); setSharedIds([]);
  };

  const openShare = async () => {
    if (!shareOpen && users.length === 0) {
      try { const r = await api.get('/users'); setUsers(r.data.filter((u) => u.user_id !== user.user_id)); } catch (e) {}
    }
    setShareOpen((v) => !v);
  };

  const shareWith = async (u) => {
    try {
      await api.post(`/appointments/${a.id}/share`, { user_id: u.user_id, user_name: u.name });
      setSharedIds((prev) => [...prev, u.user_id]);
      toast({ title: 'Alerta compartilhado', description: `${u.name} também receberá o alerta na tela.` });
    } catch (e) { toast({ title: 'Erro ao compartilhar' }); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="appointment-reminder-overlay">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative z-10 w-full max-w-lg rounded-2xl border-2 border-amber-400 bg-card p-8 text-center shadow-[0_0_80px_rgba(251,191,36,0.4)] animate-reminder-pop">
        <span className="absolute -inset-1 rounded-2xl ring-4 ring-amber-400/40 animate-reminder-ring pointer-events-none" />
        <div className="mx-auto w-20 h-20 rounded-full bg-amber-400/15 border-2 border-amber-400 flex items-center justify-center mb-5">
          <BellRing className="w-10 h-10 text-amber-400 animate-bell-shake" />
        </div>
        <p className="text-amber-400 font-black tracking-[0.25em] text-sm mb-1">⚠ LEMBRETE DE COMPROMISSO</p>
        <h2 className="font-display font-black text-3xl sm:text-4xl uppercase text-foreground mb-2" data-testid="reminder-title">
          Em {minsLeft} minuto{minsLeft > 1 ? 's' : ''}!
        </h2>
        <p className="text-lg font-bold text-foreground mb-1" data-testid="reminder-appt-title">{a.titulo}</p>
        <div className="flex items-center justify-center gap-4 text-sm text-foreground/80 mt-3 flex-wrap">
          {a.cliente && <span className="flex items-center gap-1.5 bg-muted/50 border border-border rounded-full px-3 py-1.5"><User className="w-4 h-4 text-primary" /> <b data-testid="reminder-client-name">{a.cliente}</b></span>}
          <span className="flex items-center gap-1.5 bg-muted/50 border border-border rounded-full px-3 py-1.5"><Clock className="w-4 h-4 text-primary" /> {a.hora}</span>
          <span className="flex items-center gap-1.5 bg-muted/50 border border-border rounded-full px-3 py-1.5"><CalendarDays className="w-4 h-4 text-primary" /> {a.tipo}</span>
        </div>
        {a.observacoes && <p className="text-xs text-muted-foreground mt-4 bg-muted/40 border border-border/70 rounded-lg px-3 py-2">{a.observacoes}</p>}
        {alerts.length > 1 && <p className="text-[11px] text-amber-300/80 mt-3">+ {alerts.length - 1} outro(s) compromisso(s) na próxima hora</p>}

        {shareOpen && (
          <div className="mt-5 rounded-xl border border-border bg-popover p-3 text-left max-h-52 overflow-y-auto" data-testid="reminder-share-list">
            <p className="text-[11px] tracking-[0.2em] text-muted-foreground font-semibold mb-2 px-1">COMPARTILHAR ALERTA COM:</p>
            {users.map((u) => {
              const done = sharedIds.includes(u.user_id) || (a.compartilhadoCom || []).some((x) => x.user_id === u.user_id);
              return (
                <button key={u.user_id} data-testid={`reminder-share-user-${u.user_id}`} disabled={done} onClick={() => shareWith(u)}
                  className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg transition-colors ${done ? 'opacity-60 cursor-default' : 'hover:bg-muted/50'}`}>
                  <span className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10px] font-bold shrink-0">{(u.name || '?').split(' ').slice(0, 2).map((x) => x[0]).join('').toUpperCase()}</span>
                  <span className="flex-1 text-left"><span className="block text-sm font-medium">{u.name}</span><span className="block text-[10px] text-muted-foreground">{u.role}</span></span>
                  {done ? <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-400"><Check className="w-3.5 h-3.5" /> Enviado</span> : <Share2 className="w-4 h-4 text-muted-foreground" />}
                </button>
              );
            })}
            {users.length === 0 && <p className="text-sm text-muted-foreground/70 px-1 py-2">Nenhum outro usuário.</p>}
          </div>
        )}

        <div className="flex gap-3 mt-6">
          <button data-testid="reminder-share-btn" onClick={openShare} className={`flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl font-bold text-sm border-2 transition-colors ${shareOpen ? 'border-primary bg-primary/15 text-primary' : 'border-border text-foreground/75 hover:text-foreground hover:border-primary/50'}`}>
            <Share2 className="w-4 h-4" /> Compartilhar
          </button>
          <button data-testid="reminder-dismiss-btn" onClick={dismiss} className="flex-1 bg-amber-400 hover:bg-amber-300 text-black font-black text-base tracking-wide py-3.5 rounded-xl transition-colors">
            OK, ESTOU CIENTE
          </button>
        </div>
      </div>
    </div>
  );
};
